import type { ICandidate } from "@bbj-types/candidate";
import { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import { Visibility } from "@mui/icons-material";
import { NoteAddOutlined } from "@mui/icons-material";
import LanguageIcon from "@mui/icons-material/Language";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { Authorizer, useAuthorizer } from "@navigations/Authorizer";
import type { ISocialApplyOffer } from "@root/api/services/offers/socialOffer/types";
import { useGlobalContext } from "@root/context";
import { isInternalOfferSource } from "@root/utils/enums/offer";
import dompurify from "isomorphic-dompurify";
import { type Dispatch, type SetStateAction, useMemo } from "react";
import { InfoCard, infoCardClasses } from "../../../offers/Document/styles";
import { useDrawerSocialOffer } from "./logics";

interface IDrawerSocialOfferBodyRegisterProps {
  offer: ISocialApplyOffer & {
    applyCreatedAt?: Date;
  };
  expired: boolean;
  candidate: ICandidate | undefined;
  setSocialApplyOfferFupModal?: Dispatch<SetStateAction<ISocialApplyOffer | null>>;
}

export const DrawerSocialOfferBodyRegister = ({
  offer,
  expired,
  candidate,
  setSocialApplyOfferFupModal,
}: IDrawerSocialOfferBodyRegisterProps) => {
  const { user } = useGlobalContext();
  const { handleGetDocument } = useDrawerSocialOffer();
  const isPartnerAuthorized = useAuthorizer(["partner.dashboard.visualize"]);

  const displayCreateFup = useMemo(() => {
    if (user.organisation.fup?.onlyManual && !isInternalOfferSource(offer.source)) {
      return false;
    }
    if (isPartnerAuthorized) {
      return Boolean(
        candidate &&
          offer.provider?.enableFup &&
          (offer.status === SocialApplyStatus.PROPOSED ||
            offer.status === SocialApplyStatus.INTERESTED_CANDIDATE ||
            (!offer.socialApplyId && offer.provider?.enableFup))
      );
    }

    return Boolean(
      candidate &&
        (offer.status === SocialApplyStatus.PROPOSED ||
          offer.status === SocialApplyStatus.INTERESTED_CANDIDATE ||
          (!offer.socialApplyId && offer.provider?.enableFup))
    );
  }, [candidate, offer, user.organisation.fup?.onlyManual, isPartnerAuthorized]);

  const displayTransmittedFup = useMemo(
    () => offer.status === SocialApplyStatus.PRESCRIPTION_FILLED && offer.fup?.document,
    [offer.status, offer.fup]
  );
  const displayCompletedFup = useMemo(
    () =>
      (offer.status === SocialApplyStatus.INTEGRATED_CANDIDATE ||
        offer.status === SocialApplyStatus.REFUSED_STRUCTURE) &&
      offer.fup?.document,
    [offer.status, offer.fup]
  );

  if (!offer.registrationModalities && !offer.url && !displayCreateFup && !displayCompletedFup) {
    return null;
  }

  return (
    <>
      <InfoCard className={infoCardClasses.root}>
        <Typography variant="subtitle2SemiBold">Comment s'inscrire ?</Typography>
        {offer.registrationModalities && (
          <div
            style={{ overflowWrap: "anywhere" }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
            dangerouslySetInnerHTML={{ __html: dompurify.sanitize(offer.registrationModalities) }}
          />
        )}
        <Authorizer requiredPermissions={["offers.social.prescription.operator"]}>
          {displayCreateFup && (
            <Button
              component="a"
              target="_blank"
              rel="noopener"
              color="primary"
              variant="outlined"
              size="small"
              disabled={expired}
              sx={{ alignSelf: "start", backgroundColor: "white" }}
              onClick={(e) => {
                e.stopPropagation();
                setSocialApplyOfferFupModal?.(offer);
              }}
            >
              <NoteAddOutlined color="primary" fontSize="small" sx={{ marginRight: "4px" }} />
              prescription
            </Button>
          )}
        </Authorizer>
        {(displayTransmittedFup || displayCompletedFup) && (
          <Tooltip title={`Voir la prescription ${displayTransmittedFup ? "transmise" : "complétée"}`}>
            <Stack>
              <Button
                component="a"
                onClick={(e) => {
                  e.stopPropagation();
                  offer.fup?.document && handleGetDocument(offer.fup?.document);
                }}
                target="_blank"
                rel="noopener"
                color="primary"
                variant="outlined"
                size="small"
                sx={{ alignSelf: "start", backgroundColor: "white" }}
                disabled={expired}
              >
                <Visibility color="primary" fontSize="small" sx={{ marginRight: "4px" }} />
                PRESCRIPTION {displayTransmittedFup ? "TRANSMISE" : "COMPLÉTÉE"}
              </Button>
            </Stack>
          </Tooltip>
        )}
        {offer.url && (
          <Tooltip title={expired && "Vous n’avez plus accès au lien d’inscription à l’offre car elle est expirée."}>
            <Stack>
              <Button
                component="a"
                href={offer.url}
                target="_blank"
                rel="noopener"
                color="primary"
                variant="outlined"
                size="small"
                sx={{ alignSelf: "start", backgroundColor: "white" }}
                disabled={expired}
              >
                <LanguageIcon color="primary" fontSize="small" sx={{ marginRight: "4px" }} />
                Inscription sur site
              </Button>
            </Stack>
          </Tooltip>
        )}
      </InfoCard>
    </>
  );
};
